import {useEffect, useState} from 'react';
import LoadingSpinner from '../components/loader-spinner';
import {Heading} from '../components/tailwind/heading';
import {Text} from '../components/tailwind/text';

type MaintenanceStatus = 'checking' | 'maintenance' | 'ready' | 'expired';

export default function MaintenancePage() {
    const [status, setStatus] = useState<MaintenanceStatus>('checking');

    useEffect(() => {
        const checkMaintenance = async () => {
            try {
                // Get token from localStorage (same pattern as existing services)
                const token = localStorage.getItem('authToken');

                // Check maintenance status via dedicated endpoint
                const response = await fetch('/api/maintenance/status', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token && {'Authorization': `Bearer ${token}`})
                    }
                });

                // Handle token expiration during maintenance
                if (response.status === 401) {
                    setStatus('expired');
                    return;
                }

                // Check if maintenance mode is over
                if (response.ok) {
                    const data = await response.json();
                    if (!data.inMaintenance) {
                        setStatus('ready');
                        // Wait a brief moment to show "ready" state, then redirect
                        setTimeout(() => {
                            window.location.href = '/';
                        }, 500);
                    } else {
                        setStatus('maintenance');
                    }
                } else {
                    // Other errors - assume still in maintenance
                    setStatus('maintenance');
                }
            } catch (_error) {
                // Network error or other issue - assume still in maintenance
                setStatus('maintenance');
            }
        };

        // Initial check
        checkMaintenance();

        // Poll every 5 seconds
        const interval = setInterval(checkMaintenance, 5000);

        // Cleanup interval on unmount
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="max-w-md w-full space-y-8 p-8">
                <div className="text-center">
                    <Heading level={1} className="text-gray-900 dark:text-white">
                        System wird aktualisiert
                    </Heading>

                    <Text className="mt-4 text-gray-600 dark:text-gray-400">
                        Wir importieren gerade Daten. Das System ist in wenigen Minuten wieder verfügbar.
                    </Text>

                    {status === 'maintenance' && (
                        <div className="mt-8">
                            <div className="flex justify-center">
                                <LoadingSpinner
                                    size={48}
                                    className="text-blue-600 dark:text-blue-400"
                                />
                            </div>
                            <Text className="mt-4 text-sm text-gray-500 dark:text-gray-500">
                                Automatische Prüfung läuft...
                            </Text>
                        </div>
                    )}

                    {status === 'ready' && (
                        <div className="mt-8">
                            <div className="bg-green-50 dark:bg-green-950 border-l-4 border-green-400 dark:border-green-500 p-4">
                                <Text className="text-green-700 dark:text-green-300">
                                    System ist wieder verfügbar. Sie werden weitergeleitet...
                                </Text>
                            </div>
                        </div>
                    )}

                    {status === 'checking' && (
                        <div className="mt-8">
                            <div className="flex justify-center">
                                <LoadingSpinner
                                    size={48}
                                    className="text-gray-400 dark:text-gray-600"
                                />
                            </div>
                        </div>
                    )}

                    {status === 'expired' && (
                        <div className="mt-8">
                            <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4">
                                <Text className="text-blue-700 dark:text-blue-300">
                                    Wartung läuft noch. Bitte melden Sie sich nach Abschluss der Wartung erneut an.
                                </Text>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
